import { Injectable } from '@angular/core';
import IResource from '@app/core/model/resource';
import { ResourceType } from '@app/core/model/resource-type';
import { BaseService } from '@app/core/services/base.service';
import { ResourceService } from '@app/core/services/toolbar/resource.service';
import { BioSamplesStore } from '@app/core/store/biosamples/biosamples.store';
import { ProjectsStore } from '@app/core/store/projects/projects.store';
import { ConsoleLogger } from '@app/core/utilities/consolelogger';
import { BasespaceService } from '@bssh/ng-sdk';
import { V2CreateBiologicalSampleRequest } from '@bssh/ng-sdk/api/models/v2create-biological-sample-request';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';
import { RadCreateResourceModalComponent } from '@root/src/app/shared/modals/rad-create-resource-modal/rad-create-resource-modal.component';
import { ModalTexts } from '../modal-texts';
import { CreateResourceModalInput, CreateResourceModalOutput, IModalOutput } from '../model/action-modal';
import { ModalService } from './modal.service';
import { ModalComponentType } from '../base-modal/base-modal.component';
import { CreateResourceModalComponent } from '../create-resource-modal/create-resource-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ChooserModalService extends BaseService {

  constructor(
    private resourceService: ResourceService,
    private modalService: ModalService
  ) {
    super();
  }

  /**
   * Handles showing a new resource creation modal
   * @param newEvent The new event from the chooser modal
   * @param resourceType The resource type which needs to be create
   */
  public createNewResourceFromChooser(
    newEvent: Observable<IModalOutput>,
    modalInput: CreateResourceModalInput,
    postNewResourceCreationAction: (data: IResource) => any,
    modalComponentType?: ModalComponentType
  ): void {
    const { resourceType } = modalInput;

    const store = this.resourceService.getStoreForResourceType(resourceType);
    if (isNullOrUndefined(store)) {
      return;
    }

    this.subs.sink = newEvent.pipe(
      tap(() =>  {
        // Close the Chooser modal.
        this.modalService.handleError(null);
      }),
      switchMap(() => {
        return this.openNewResourceModal(modalInput, modalComponentType);
      })
    )
    .subscribe(
      {
        next: (data) => {
          // Invoke the passed in arrow function, to handle the post resource creation action
          postNewResourceCreationAction(data);
        },
        error: (errorObj) => {
          ConsoleLogger.logMessage('Error in Create Resource Modal', errorObj);
        }
      });
  }

  public openNewResourceModal(createResourceModalInput: CreateResourceModalInput, modalComponentType?: ModalComponentType): Observable<any> {
    const { resourceType } = createResourceModalInput;
    let modalComponent = !!modalComponentType  && modalComponentType === ModalComponentType.RAD ? RadCreateResourceModalComponent : CreateResourceModalComponent;

    return this.modalService.openModal(createResourceModalInput, modalComponent as any)
        .confirm
        .pipe(
          switchMap((output: CreateResourceModalOutput) => {
            let createNewResourceParams: BasespaceService.PostV2ProjectsParams | V2CreateBiologicalSampleRequest;

            switch(resourceType) {
                case ResourceType.PROJECT:
                case ResourceType.PROJECTS:
                  createNewResourceParams = {
                    name: output.fieldValueMap.Name,
                    description: output.fieldValueMap.Description
                  } as BasespaceService.PostV2ProjectsParams
                  break;
                case ResourceType.BIO_SAMPLE:
                case ResourceType.BIO_SAMPLES:
                  createNewResourceParams = {
                    // Trimming of name happens for new project, server side but for new biosample, occurs client side on old UI
                    BioSampleName: output.fieldValueMap.Name.trim(),
                    ContainerName: output.fieldValueMap.Container,
                    ContainerPosition: output.fieldValueMap.Position,
                    DefaultProjectId: output.defaultProject.Id,
                    SubjectId: null
                  } as V2CreateBiologicalSampleRequest
                  break;
              }
            
            return this.createNewResource(createResourceModalInput.resourceType, createNewResourceParams);
          }),
        );
  }

  /**
   * Creates a new resource by invoking the respective resource's store.
   */
  private createNewResource(resourceType: ResourceType, params: BasespaceService.PostV2ProjectsParams | V2CreateBiologicalSampleRequest): Observable<IResource> {
    switch (resourceType) {
      case ResourceType.PROJECT:
      case ResourceType.PROJECTS:
        const projectStore = this.resourceService.getStoreForResourceType(resourceType) as ProjectsStore;
        return projectStore.createNew(params as BasespaceService.PostV2ProjectsParams).pipe(
          catchError(errorObj => {
            // Display error message on the new resource modal and keep it open
            const errorMessage = errorObj.error && errorObj.error.ErrorMessage ? errorObj.error.ErrorMessage :
              ModalTexts.CHOOSER.ERROR_CREATING_NEW_RESOURCE('Project');
            this.modalService.handleError(errorMessage);
            return EMPTY;
          }));
      case ResourceType.BIO_SAMPLE:
      case ResourceType.BIO_SAMPLES:
        const biosamplesStore = this.resourceService.getStoreForResourceType(resourceType) as BioSamplesStore;
        return biosamplesStore.createNew(params as V2CreateBiologicalSampleRequest).pipe(
          catchError(errorObj => {
            // Display error message on the new resource modal and keep it open
            const errorMessage = errorObj.error && errorObj.error.ErrorMessage ? errorObj.error.ErrorMessage :
              ModalTexts.CHOOSER.ERROR_CREATING_NEW_RESOURCE('Biosample');
            this.modalService.handleError(errorMessage);
            return EMPTY;
          }));
      default:
        return throwError('Resource Type not supported');
    }
  }
}
